<template>
  <div>
    <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
      <div
        :class="['content', { clickable: isClickable }, { 'content-black' : isDarkTheme }]"
        v-bind="accessibilityAttrs"
        @click.capture="handleTileLink"
        @keyup.enter="handleTileLink"
      >
        <div v-if="isVideo" class="video-container">
          <div v-if="isEditMode" class="video-placeholder"></div>
          <vimeo-video
            v-else
            :class="ratioClass"
            :url="videoUrl"
            :autoplay="autoplay"
            :loop="loop"
            :mute="mute || autoplay"
            :defaultControls="!autoplay"
            :hidden-text="hiddenVideoTitle"
            @error="isVideoFailed = true"
          >
            <picture>
              <source media="(max-width: 767px)" :srcset="mobileImage.cloudinary_3x4_fill_crop">
              <img class="img" :src="desktopImage.cloudinary_5x3_fill_crop" :alt="desktopImage.alt"/>
            </picture>
          </vimeo-video>
        </div>
        <div v-else class="image-container" :class="{ 'live-mode' : !isEditMode }">
          <sc-image v-if="isEditMode" :media="desktopImage || {}"/>
          <picture v-else>
            <source
              media="(max-width: 767px)"
              :srcset="mobileImage.cloudinary_3x4_fill_crop || desktopImage.cloudinary_3x4_fill_crop"
            >
            <img class="img" :src="desktopImage.cloudinary_5x3_fill_crop" :alt="desktopImage.alt"/>
          </picture>
        </div>
        <div :class="getTextPosition">
          <div v-if="isEditMode || title" :class="['title', {[`title--${titleColor}`]: !!titleColor}]">
            <heading
              :tagField="fields.headingSize"
              :textField="fields.title"
              :class="[textThemeClass, { 'with-hint': isEditMode} ]"
              mobile-size="size-5"
              desktop-size="size-2"
            />
          </div>
          <div v-if="isEditMode || cta1.href || cta2.href" class="controls" :class="textThemeClass">
            <div
              v-if="cta1.href"
              ref="cta1"
              class="cta1-wrapper"
              @click.capture="openOverlay($event, cta1)"
              @keypress.enter="openOverlay($event, cta1)"
            >
              <CTA
                class='cta'
                :ctaFields="cta1"
                :ctaColor="cta1Color"
                :ctaAppearance="ctaAppearance"
              />
            </div>
            <div
              v-if="cta2.href"
              ref="cta2"
              class="cta2-wrapper"
              @click.capture="openOverlay($event, cta2)"
              @keypress.enter="openOverlay($event, cta2)"
            >
              <CTA
                class='cta'
                :ctaColor="cta2Color"
                :ctaFields="cta2"
                :ctaAppearance="ctaAppearance"
              />
            </div>
          </div>
        </div>
      </div>
    </sc-render>

    <MemberSessionModal
      :name="memberSessionModalName"
      :routeOnConfirm="redirectUrl"
      :routeOnCancel="redirectUrlOnCancel"
    >
      <template v-if="membersOnlyBooking">
        {{ $t('OVERLAY_BLOCK_YOU_MUST_BE_A_MEMBER') }}
      </template>
    </MemberSessionModal>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { Image, Text } from '@sitecore-jss/sitecore-jss-vue'
import { v4 as uuid } from 'uuid'

import isEmptyObject from '~/helpers/is-empty-object'
import UIMixin from '~/mixins/ui'
import get from '~/helpers/get'
import mapFields from '~/helpers/map-fields'
import VimeoVideo from '~/components/sitecore/vimeo-video'
import CTA from '~/components/sitecore/cta'
import ScRender from '~/components/sitecore/sc-render'
import Heading from '~/components/sitecore/heading'
import MemberSessionModal, { MEMBER_SESSION_MODAL } from '~/components/member-session-modal'
import {
  ROOM_BOOKING_TYPE,
  ARE_YOU_A_MEMBER,
  MEMBER_SESSION_SHOWN
} from '~/constants/sitecore-keys'

const { mapState: mapJssState, mapGetters: mapJssGetters } = createNamespacedHelpers('jss')
const { mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')
const { mapGetters: mapProfileGetters } = createNamespacedHelpers('user/profile')

const defaultPosition = 'below'
const RATIO = {
  '9:16': '9x16',
  '3:4': '3x4',
  '16:9': '16x9'
}

export default {
  name: 'Hero',

  components: {
    scText: Text,
    ScImage: Image,
    ScRender,
    CTA,
    Heading,
    VimeoVideo,
    MemberSessionModal
  },

  mixins: [UIMixin],

  props: {
    fields: {
      type: Object,
      default: () => ({})
    },
    rendering: {
      type: Object
    }
  },

  data () {
    return {
      isVideoFailed: false,
      redirectUrl: ''
    }
  },

  computed: {
    ...mapProfileGetters(['profile']),
    ...mapJssState(['route']),
    ...mapJssGetters([
      'isEditMode'
    ]),
    ...mapEnvGetters(['isDarkTheme']),
    ...mapFields({
      textPosition: 'textPosition.value',
      ctaAppearance: 'ctaAppearance.value',
      title: 'title.value',
      titleColor: 'textColour.fields.designSystemToken.value',
      cta1Color: 'button1Color.value',
      cta2Color: 'button2Color.value'
    }),
    ratioClass () {
      return this.isMobile ? this.mobileVideoClass : this.desktopVideoClass
    },

    // Only if autoplay option is selected, mobile video will be showed on mobile
    // If desktop video has ratio 9:16 it should 9:16 on mobile
    // In other case desktop video will be showed on mobile in ratio 9:16
    mobileVideoClass () {
      if (this.showMobileVideo) return `mobile-${RATIO[this.mobileVideoRatio]}`
      if (RATIO[this.desktopVideoRatio] === RATIO['9:16']) return `mobile-${RATIO['9:16']}`
      return `mobile-${RATIO['16:9']}`
    },

    desktopVideoClass () {
      return `desktop-${RATIO[this.desktopVideoRatio]}`
    },

    mobileVideoRatio () {
      return get(this.fields, 'mobileVideoAspectRatio.fields.aspectRatio.value', '9:16')
    },

    desktopVideoRatio () {
      return get(this.fields, 'desktopVideoAspectRatio.fields.aspectRatio.value', '16:9')
    },

    videoUrl () {
      return this.showMobileVideo ? this.mobileVideoUrl : this.desktopVideoUrl
    },

    isVideo () {
      return (this.showMobileVideo || this.showDesktopVideo) && !this.isVideoFailed
    },

    showDesktopVideo () {
      return this.desktopVideoUrl && (!this.$device.isMobile || !this.autoplay)
    },

    showMobileVideo () {
      return this.$device.isMobile && this.autoplay && this.mobileVideoUrl
    },

    mobileImage () {
      return get(this.fields, 'mobileImage.value', {})
    },

    desktopVideoUrl () {
      return get(this.fields, 'mainVideo.value', '')
    },

    mobileVideoUrl () {
      return get(this.fields, 'mobileVideo.value', '')
    },

    autoplay () {
      return get(this.fields, 'autoplay.value', false)
    },

    loop () {
      return get(this.fields, 'loop.value', false)
    },

    mute () {
      return get(this.fields, 'mute.value', false)
    },

    hiddenVideoTitle () {
      return get(this.fields, 'hiddenVideoTitle.value', '')
    },

    desktopImage () {
      return get(this.fields, 'mainImage.value', {})
    },

    cta1 () {
      return get(this.fields, 'cta1.value', {})
    },

    cta2 () {
      return get(this.fields, 'cta2.value', {})
    },

    mainImage () {
      return this.fields.mainImage || {}
    },

    getTextPosition () {
      return (this.textPosition && this.textPosition.split(' ')[1]) || defaultPosition
    },

    isClickable () {
      return (this.cta2.href || this.cta1.href)
        && !(this.cta2.href && this.cta1.href)
        && !this.videoUrl
    },

    textThemeClass () {
      return this.isDarkTheme ? 'black-theme-text' : 'white-theme-text'
    },

    accessibilityAttrs () {
      if (this.videoUrl) {
        return { tabindex: '0' }
      }

      return this.isClickable ? { role: 'link', tabindex: '0' } : {}
    },

    membersOnlyBooking () {
      return get(this.route, 'fields.venue.fields.publicBooking.value', {}) === ROOM_BOOKING_TYPE.closed
    },

    hasProfile () {
      return this.profile && !isEmptyObject(this.profile)
    },

    memberSessionModalName () {
      return `${MEMBER_SESSION_MODAL}-${uuid()}`
    },

    // If the venue is members only and the user presses 'I'm not a member'
    // then redirect to the /bedrooms page without the attached
    // location query in the redirectUrl provided by Sitecore
    redirectUrlOnCancel () {
      return this.membersOnlyBooking
        ? this.localePath({ path: '/bedrooms' })
        : this.redirectUrl
    }
  },

  methods: {
    handleTileLink () {
      if (!this.isClickable) return

      // This is a fallback in case the assign it to either cta1 or cta2 in Sitecore
      // In theory they use just use cta1 - but this is to catch an edge case
      const ctaRef = this.$refs.cta1 || this.$refs.cta2
      ctaRef.firstChild.click()
    },

    openOverlay (event, cta) {
      if (sessionStorage.getItem(MEMBER_SESSION_SHOWN) === 'true'
        || this.hasProfile
        || cta.overlaybehavior !== ARE_YOU_A_MEMBER
      ) {
        return
      }

      event.preventDefault()
      this.redirectUrl = cta.href
      this.$ui.modal.open(this.memberSessionModalName)
    }
  }
}
</script>

<style lang="scss" scoped>
.content {
  position: relative;
  margin-bottom: $space-090;

  @include tablet() {
    margin-bottom: $space-100;
  }

  .bottom {
    position: relative;
    bottom: auto;
    left: auto;
    padding-left: $space-050;

    @include tablet() {
      position: absolute;
      bottom: $space-110;
      padding-left: $space-060;
    }
  }

  .below {
    padding-left: $space-050;

    @include tablet() {
      padding-left: $space-060;
    }
  }

  .image-container {

    .img {
      display: block;
      width: 100%;
    }

    &.live-mode {
      position: relative;
      @include ratio(3, 4);

      @include tablet {
        @include ratio(5, 3);
      }

      .img {
        position: absolute;
        left: 0;
        top: 0;
      }
      @include tablet() {
        @include ratio(5, 3);
      }
    }
    ::v-deep img {
      object-fit: cover;
      width: 100%;
    }
  }

  .video-container {
    .video-placeholder {
      width: 100%;
      min-height: 75vh;
      @include placeholder-image('~assets/images/video-placeholder.png');
    }

    .mobile-3x4 {
      @include ratio(3, 4);
    }
    .mobile-9x16 {
      @include ratio(9, 16);
    }
    .desktop-9x16 {
      @include ratio(16, 9);
      background-color: $color-gray-2;
      border-top: $space-060 solid $color-gray-2;
      border-bottom: $space-060 solid $color-gray-2;
    }

    .desktop-16x9{
      @include ratio(16, 9);
    }
  }

  .title {
    padding-top: $space-050;

    &--light {
      color: $color-cream;
    }

    &--dark {
      color: $color-black;
    }

    &.with-hint {
      padding-top: $space-040;
      @include font('100');
    }
  }

  .black-theme-text {
    color: $color-cream;

    @include tablet() {
      color: inherit;
    }
  }

  .white-theme-text {
    color: $color-black;

    @include tablet() {
      color: inherit;
    }
  }

  .controls {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 0;

    @include tablet() {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-top: $space-060;
    }

    .cta {
      margin-top: $space-030;

      @include tablet() {
        margin: 0 $space-040 $space-020 0;
      }
    }
  }
}

.content-black {
  @include outline-focus ($color-white)
}

.clickable {
  cursor: pointer;
}

</style>
